import {
  classifyError,
  type ErrorKind,
  errorGuidanceFa,
  errorMessageFa,
  errorRequestId,
  type FieldError as ApiFieldError,
  isApiError,
  SESSION_RATE_LIMITED_FA,
  UnknownResultError,
} from '@saay/api-client';
import {
  displayServerMask,
  formatCount,
  formatTimestamp,
  formatToman,
  formatTomanCompact,
  type MaskedKind,
  type MaskSpec,
  type Rials,
  resolveStatus,
  type StatusEnum,
  type StatusTone,
  statusLabel,
  toPersianDigits,
} from '@saay/formatting';
import {
  CardFaceRows,
  IconTile,
  SignatureCard,
  StatusBadge,
  Surface,
  type Tone as BlockTone,
} from '@saay/blocks';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@saay/shadcn/components/accordion';
import {
  AlertDialog as ShadAlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@saay/shadcn/components/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@saay/shadcn/components/alert';
import { Avatar, AvatarFallback } from '@saay/shadcn/components/avatar';
import { Button as ShadButton } from '@saay/shadcn/components/button';
import {
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@saay/shadcn/components/card';
import { Checkbox as ShadCheckbox } from '@saay/shadcn/components/checkbox';
import {
  Dialog as ShadDialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@saay/shadcn/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@saay/shadcn/components/dropdown-menu';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@saay/shadcn/components/empty';
import { Field, FieldDescription, FieldError, FieldLabel } from '@saay/shadcn/components/field';
import { Input } from '@saay/shadcn/components/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@saay/shadcn/components/input-group';
import {
  NativeSelect as ShadNativeSelect,
  NativeSelectOption,
} from '@saay/shadcn/components/native-select';
import { Progress } from '@saay/shadcn/components/progress';
import { RadioGroup, RadioGroupItem } from '@saay/shadcn/components/radio-group';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@saay/shadcn/components/sheet';
import { Skeleton as ShadSkeleton } from '@saay/shadcn/components/skeleton';
import { Switch as ShadSwitch } from '@saay/shadcn/components/switch';
import {
  Table as ShadTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@saay/shadcn/components/table';
import { Tabs as ShadTabs, TabsContent, TabsList, TabsTrigger } from '@saay/shadcn/components/tabs';
import { Textarea } from '@saay/shadcn/components/textarea';
import { DateRangePicker as SolarDateRangePicker, shouldShowRequestId, useTheme } from '@saay/ui';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ClockIcon,
  CopyIcon,
  CreditCardIcon,
  DownloadIcon,
  EyeIcon,
  FileTextIcon,
  InboxIcon,
  InfoIcon,
  LandmarkIcon,
  type LucideIcon,
  MoonIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
  RefreshCwIcon,
  SearchIcon,
  Share2Icon,
  ShieldIcon,
  StoreIcon,
  SunIcon,
  TriangleAlertIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import {
  type ButtonHTMLAttributes,
  type ChangeEvent,
  cloneElement,
  type ComponentProps,
  createContext,
  forwardRef,
  type InputHTMLAttributes,
  isValidElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

export {
  applyFieldErrors,
  Combobox,
  Countdown,
  EditableGrid,
  EntityPicker,
  FileImportField,
  MoneyField,
  MultiSelectCombobox,
  NumberField,
  parseDelimitedImport,
  PercentField,
  PHONE_QUERY,
  SolarDateInput,
  toFormPath,
  useMediaQuery,
  useSearchInput,
  useSnackbar,
  useToast,
  useWindowClass,
} from '@saay/ui';
export type {
  ButtonVariant,
  DateRangeValue,
  DialogSize,
  DiffRow,
  EntityPickerOption,
  FileImportPreview,
  IconName,
} from '@saay/ui';

const ICONS: Record<string, LucideIcon> = {
  alert: TriangleAlertIcon,
  'arrow-back': ArrowRightIcon,
  'arrow-left': ArrowLeftIcon,
  bank: LandmarkIcon,
  card: CreditCardIcon,
  check: CheckIcon,
  'chevron-down': ChevronDownIcon,
  'chevron-left': ChevronLeftIcon,
  'chevron-right': ChevronLeftIcon,
  clock: ClockIcon,
  close: XIcon,
  copy: CopyIcon,
  doc: FileTextIcon,
  download: DownloadIcon,
  edit: PencilIcon,
  eye: EyeIcon,
  info: InfoIcon,
  more: MoreHorizontalIcon,
  plus: PlusIcon,
  printer: PrinterIcon,
  refresh: RefreshCwIcon,
  search: SearchIcon,
  share: Share2Icon,
  shield: ShieldIcon,
  store: StoreIcon,
  users: UsersIcon,
};

export function Icon({ className, name }: { readonly className?: string; readonly name: string }) {
  const Glyph = ICONS[name];
  if (Glyph === undefined) return <span aria-hidden="true" className={className} />;
  return <Glyph aria-hidden="true" className={className ?? 'size-4'} />;
}

const BUTTON_VARIANT = {
  danger: 'destructive',
  elevated: 'secondary',
  filled: 'default',
  ghost: 'ghost',
  outlined: 'outline',
  primary: 'default',
  secondary: 'outline',
  text: 'ghost',
  tonal: 'secondary',
} as const;

type ButtonAppearance = keyof typeof BUTTON_VARIANT;

export const Button = forwardRef<
  HTMLButtonElement,
  {
    readonly blocked?: boolean;
    readonly blockedReason?: string;
    readonly fullWidth?: boolean;
    readonly icon?: string;
    readonly loading?: boolean;
    readonly size?: 'sm' | 'md';
    readonly trailingIcon?: string;
    readonly variant?: ButtonAppearance;
  } & ButtonHTMLAttributes<HTMLButtonElement>
>(function Button(
  {
    blocked = false,
    blockedReason,
    children,
    className,
    disabled,
    fullWidth,
    icon,
    loading = false,
    onClick,
    size = 'md',
    title,
    trailingIcon,
    type = 'button',
    variant = 'primary',
    ...rest
  },
  ref,
) {
  const Glyph = icon === undefined ? undefined : ICONS[icon];
  const Trailing = trailingIcon === undefined ? undefined : ICONS[trailingIcon];
  return (
    <ShadButton
      {...rest}
      aria-busy={loading || undefined}
      aria-disabled={blocked || undefined}
      className={
        [fullWidth ? 'w-full' : undefined, className].filter(Boolean).join(' ') || undefined
      }
      disabled={disabled === true || loading}
      onClick={(event) => {
        if (blocked) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      ref={ref}
      size={size === 'sm' ? 'sm' : 'default'}
      title={blocked && blockedReason !== undefined ? blockedReason : title}
      type={type}
      variant={BUTTON_VARIANT[variant]}
    >
      {Glyph === undefined || loading ? null : <Glyph aria-hidden="true" />}
      {children}
      {Trailing === undefined || loading ? null : <Trailing aria-hidden="true" />}
    </ShadButton>
  );
});

const ICON_BUTTON_VARIANT = {
  filled: 'default',
  outlined: 'outline',
  standard: 'ghost',
  tonal: 'secondary',
} as const;

export const IconButton = forwardRef<
  HTMLButtonElement,
  {
    readonly blocked?: boolean;
    readonly blockedReason?: string;
    readonly icon: string;
    readonly label: string;
    readonly loading?: boolean;
    readonly variant?: keyof typeof ICON_BUTTON_VARIANT;
  } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>
>(function IconButton(
  {
    blocked = false,
    blockedReason,
    className,
    icon,
    label,
    loading = false,
    onClick,
    type = 'button',
    variant = 'standard',
    ...rest
  },
  ref,
) {
  const Glyph = ICONS[icon] ?? MoreHorizontalIcon;
  return (
    <ShadButton
      {...rest}
      aria-busy={loading || undefined}
      aria-disabled={blocked || undefined}
      aria-label={label}
      className={className}
      disabled={rest.disabled === true || loading}
      onClick={(event) => {
        if (blocked) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      ref={ref}
      size="icon"
      title={blocked && blockedReason !== undefined ? blockedReason : label}
      type={type}
      variant={ICON_BUTTON_VARIANT[variant]}
    >
      {loading ? null : <Glyph aria-hidden="true" />}
    </ShadButton>
  );
});

export function ThemeToggle({ className }: { readonly className?: string }) {
  const { theme, toggle } = useTheme();
  const dark = theme === 'dark';
  return (
    <ShadButton
      aria-label="حالت تیره"
      aria-pressed={dark}
      {...(className === undefined ? {} : { className })}
      onClick={toggle}
      size="icon"
      type="button"
      variant="ghost"
    >
      {dark ? <SunIcon aria-hidden="true" /> : <MoonIcon aria-hidden="true" />}
    </ShadButton>
  );
}

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

export function OfflineBanner() {
  const online = useOnlineStatus();
  if (online) return null;
  return (
    <Alert>
      <TriangleAlertIcon aria-hidden="true" />
      <AlertDescription>
        اینترنت وصل نیست. تا وصل شدن دوباره، اطلاعات مالی به‌روز نمی‌شود و کارهای مالی غیرفعال است.
      </AlertDescription>
    </Alert>
  );
}

export function Ltr({ children }: { readonly children: ReactNode; readonly mask?: boolean }) {
  return (
    <bdi dir="ltr" translate="no">
      {children}
    </bdi>
  );
}

export function Masked({
  kind = 'text',
  value,
}: {
  readonly kind?: MaskedKind;
  readonly value: string;
}) {
  return <Ltr>{displayServerMask(value, kind)}</Ltr>;
}

export function Money({
  compact = false,
  rials,
}: {
  readonly compact?: boolean;
  readonly rials: Rials;
}) {
  return (
    <span className="font-bold tabular-nums">
      {compact ? formatTomanCompact(rials) : formatToman(rials)}
    </span>
  );
}

export function Skeleton({
  label = 'در حال بارگذاری…',
  lines = 3,
}: {
  readonly label?: string;
  readonly lines?: number;
}) {
  return (
    <div aria-busy="true" aria-live="polite" className="grid gap-2" role="status">
      <span className="sr-only">{label}</span>
      {Array.from({ length: lines }, (_, index) => (
        <ShadSkeleton className="h-4 w-full" key={index} />
      ))}
    </div>
  );
}

export function SkeletonShape({
  label = 'در حال بارگذاری…',
  lines = 3,
  rows = 4,
}: {
  readonly label?: string;
  readonly lines?: number;
  readonly rows?: number;
  readonly variant?: string;
}) {
  return <Skeleton label={label} lines={rows > lines ? rows : lines} />;
}

export function Banner({
  children,
  tone,
}: {
  readonly children: ReactNode;
  readonly icon?: string;
  readonly tone: 'info' | 'success' | 'warning' | 'danger';
}) {
  return (
    <Alert variant={tone === 'danger' ? 'destructive' : 'default'}>
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

function copyableRequestId(error: unknown): string | null {
  if (!shouldShowRequestId(error)) return null;
  const id = errorRequestId(error);
  return id === null || id === '' ? null : id;
}

export function RequestId({ value }: { readonly value: string }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span>کد پیگیری:</span>
      <Ltr>
        <code>{value}</code>
      </Ltr>
      <ShadButton
        onClick={() => {
          void navigator.clipboard?.writeText(value);
        }}
        size="sm"
        type="button"
        variant="ghost"
      >
        کپی
      </ShadButton>
    </span>
  );
}

export function ErrorState({
  error,
  onRetry,
  showRetryAfter = true,
  title = 'مشکلی پیش آمد',
}: {
  readonly error: unknown;
  readonly onRetry?: () => void;
  readonly showRetryAfter?: boolean;
  readonly title?: ReactNode;
}) {
  const requestId = copyableRequestId(error);
  const kind = classifyError(error);
  const retryable =
    kind === 'offline' ||
    kind === 'unavailable' ||
    kind === 'rate-limited' ||
    (isApiError(error) && error.retryable);
  const retryAfter = isApiError(error) ? error.retryAfterSeconds : undefined;
  const guidance = errorGuidanceFa(error);
  return (
    <Alert variant="destructive">
      <AlertTitle>
        <h2>{title}</h2>
      </AlertTitle>
      <AlertDescription>
        <p>{errorMessageFa(error)}</p>
        {guidance === undefined ? null : <p>{guidance}</p>}
        {retryAfter === undefined || !showRetryAfter ? null : (
          <p>{`حدود ${formatCount(retryAfter)} ثانیه دیگر دوباره تلاش کنید.`}</p>
        )}
        {requestId === null || requestId === '' ? null : <RequestId value={requestId} />}
      </AlertDescription>
      {retryable && onRetry !== undefined ? (
        <div className="col-span-full mt-2">
          <ShadButton onClick={onRetry} type="button" variant="outline">
            تلاش دوباره
          </ShadButton>
        </div>
      ) : null}
    </Alert>
  );
}

export function AccessDenied() {
  return (
    <Alert>
      <AlertTitle>
        <h2>به این بخش دسترسی ندارید</h2>
      </AlertTitle>
      <AlertDescription>
        نقش شما اجازه دیدن این بخش را ندارد. اگر فکر می‌کنید اشتباه است، با مدیر خود تماس بگیرید.
      </AlertDescription>
    </Alert>
  );
}

function SessionExpired({ onLogin }: { readonly onLogin: () => void }) {
  return (
    <Alert>
      <AlertTitle>
        <h2>برای امنیت حساب، دوباره وارد شوید</h2>
      </AlertTitle>
      <AlertDescription>پس از ورود به همین صفحه برمی‌گردید.</AlertDescription>
      <div className="col-span-full mt-2">
        <ShadButton onClick={onLogin} type="button">
          ورود دوباره
        </ShadButton>
      </div>
    </Alert>
  );
}

function SessionRateLimited({
  error,
  onLogin,
  onRetry,
}: {
  readonly error: unknown;
  readonly onLogin: () => void;
  readonly onRetry?: () => void;
}) {
  const retryAfter = isApiError(error) ? error.retryAfterSeconds : undefined;
  const requestId = copyableRequestId(error);
  return (
    <Alert>
      <AlertTitle>
        <h2>{SESSION_RATE_LIMITED_FA.title}</h2>
      </AlertTitle>
      <AlertDescription>
        <p>{SESSION_RATE_LIMITED_FA.text}</p>
        {retryAfter === undefined ? null : (
          <p>{`حدود ${formatCount(retryAfter)} ثانیه دیگر دوباره تلاش کنید.`}</p>
        )}
        {requestId === null || requestId === '' ? null : <RequestId value={requestId} />}
      </AlertDescription>
      <div className="col-span-full mt-2 flex flex-wrap gap-2">
        {onRetry === undefined ? null : (
          <ShadButton onClick={onRetry} type="button" variant="outline">
            تلاش دوباره
          </ShadButton>
        )}
        <ShadButton onClick={onLogin} type="button" variant="ghost">
          ورود دوباره
        </ShadButton>
      </div>
    </Alert>
  );
}

export interface DataStateProps {
  readonly children: ReactNode;
  readonly empty?: ReactNode;
  readonly error: unknown;
  readonly isEmpty?: boolean;
  readonly onLogin: () => void;
  readonly onRetry?: () => void;
  readonly session?: boolean;
  readonly skeleton?: ReactNode;
  readonly status: 'pending' | 'error' | 'success';
}

export function DataState({
  children,
  empty,
  error,
  isEmpty = false,
  onLogin,
  onRetry,
  session = false,
  skeleton,
  status,
}: DataStateProps) {
  if (status === 'pending') return <>{skeleton ?? <Skeleton />}</>;
  if (status === 'error') {
    const kind: ErrorKind = classifyError(error);
    if (kind === 'unauthorized') return <AccessDenied />;
    if (kind === 'session-expired') return <SessionExpired onLogin={onLogin} />;
    if (session && isApiError(error) && error.status === 429) {
      return (
        <SessionRateLimited
          error={error}
          onLogin={onLogin}
          {...(onRetry === undefined ? {} : { onRetry })}
        />
      );
    }
    return <ErrorState error={error} {...(onRetry === undefined ? {} : { onRetry })} />;
  }
  if (isEmpty) return <>{empty ?? <EmptyState title="موردی برای نمایش نیست" />}</>;
  return <>{children}</>;
}

export function EmptyState({
  action,
  description,
  icon,
  title,
}: {
  readonly action?: ReactNode;
  readonly description?: ReactNode;
  readonly icon?: string;
  readonly title: ReactNode;
}) {
  return (
    <Empty className="rounded-2xl bg-background">
      <EmptyHeader>
        <EmptyMedia>
          <IconTile
            icon={(icon === undefined ? undefined : ICONS[icon]) ?? InboxIcon}
            tone="forest"
          />
        </EmptyMedia>
        <EmptyTitle>
          <h2 className="font-bold">{title}</h2>
        </EmptyTitle>
        {description === undefined ? null : <EmptyDescription>{description}</EmptyDescription>}
      </EmptyHeader>
      {action === undefined ? null : <EmptyContent>{action}</EmptyContent>}
    </Empty>
  );
}

export function CommandError({
  action,
  error,
}: {
  readonly action?: ReactNode;
  readonly error: unknown;
}) {
  const requestId = copyableRequestId(error);
  return (
    <Alert variant="destructive">
      <AlertDescription>
        <p>{errorMessageFa(error)}</p>
        {requestId === null || requestId === '' ? null : <RequestId value={requestId} />}
        {action}
      </AlertDescription>
    </Alert>
  );
}

export interface CommandFeedbackState {
  readonly approval?: { readonly actionType: string; readonly expiresAt: string } | undefined;
  readonly error: unknown;
  readonly status: 'idle' | 'pending' | 'success' | 'approval-pending' | 'error' | 'unknown';
}

export function CommandFeedback({
  approvalAction,
  onCheckStatus,
  pendingLabel = 'در حال ارسال…',
  state,
}: {
  readonly approvalAction?: ReactNode;
  readonly onCheckStatus: () => void;
  readonly pendingLabel?: string;
  readonly state: CommandFeedbackState;
}) {
  if (state.status === 'approval-pending' && state.approval !== undefined) {
    return (
      <Alert>
        <ClockIcon aria-hidden="true" />
        <AlertTitle>
          <h2>برای تأیید ارسال شد</h2>
        </AlertTitle>
        <AlertDescription>
          <p>
            {`درخواست «${statusLabel('approvalActionType', state.approval.actionType)}» ثبت شد و تا تأیید یک مدیر دیگر اعمال نمی‌شود. هیچ‌کس درخواست خودش را تأیید نمی‌کند.`}
          </p>
          <p>
            {'مهلت بررسی تا '}
            <time dateTime={state.approval.expiresAt}>
              {formatTimestamp(state.approval.expiresAt)}
            </time>
          </p>
          {approvalAction}
        </AlertDescription>
      </Alert>
    );
  }
  if (state.status === 'pending') {
    return (
      <p aria-live="polite" role="status">
        {pendingLabel}
      </p>
    );
  }
  if (state.status === 'unknown') {
    return (
      <Alert>
        <AlertTitle>
          <h2>نتیجه مشخص نیست</h2>
        </AlertTitle>
        <AlertDescription>
          {state.error instanceof UnknownResultError
            ? state.error.messageFa
            : 'نتیجه درخواست مشخص نیست.'}{' '}
          بررسی وضعیت همان درخواست را دوباره می‌فرستد و دوبار ثبت نمی‌شود.
        </AlertDescription>
        <div className="col-span-full mt-2">
          <ShadButton onClick={onCheckStatus} type="button" variant="outline">
            بررسی وضعیت
          </ShadButton>
        </div>
      </Alert>
    );
  }
  if (state.status === 'error') {
    return <ErrorState error={state.error} onRetry={onCheckStatus} title="ثبت انجام نشد" />;
  }
  return null;
}

export function FieldErrorList({
  errors,
  labels = {},
}: {
  readonly errors: readonly ApiFieldError[];
  readonly labels?: Readonly<Record<string, string>>;
}) {
  if (errors.length === 0) return null;
  return (
    <Banner tone="danger">
      <ul>
        {errors.map((fieldError) => (
          <li key={`${fieldError.field}:${fieldError.code}`}>
            {labels[fieldError.field] ?? null}
            {labels[fieldError.field] === undefined ? null : ': '}
            {fieldError.messageFa}
          </li>
        ))}
      </ul>
    </Banner>
  );
}

const SHEET_WIDTH = {
  default: 'w-full sm:max-w-lg',
  small: 'w-full sm:max-w-sm',
  wide: 'w-full sm:max-w-3xl',
  wizard: 'w-full sm:max-w-5xl',
} as const;

function sheetWidth(size: keyof typeof SHEET_WIDTH | undefined, fullScreen: boolean): string {
  const width = SHEET_WIDTH[size ?? 'default'];
  return fullScreen ? `${width} max-sm:h-dvh max-sm:w-full max-sm:max-w-none` : width;
}

export function Dialog({
  children,
  description,
  eyebrow,
  footer,
  fullScreenOnCompact = false,
  onOpenChange,
  open,
  size = 'default',
  title,
  variant = 'dialog',
}: {
  readonly allowDragDismiss?: boolean;
  readonly children?: ReactNode;
  readonly description?: ReactNode;
  readonly eyebrow?: ReactNode;
  readonly footer?: ReactNode;
  readonly fullScreenOnCompact?: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
  readonly size?: keyof typeof SHEET_WIDTH;
  readonly stickyFooter?: boolean;
  readonly stickyHeader?: boolean;
  readonly title: ReactNode;
  readonly trigger?: ReactNode;
  readonly variant?: 'dialog' | 'sheet';
}) {
  const eyebrowNode =
    eyebrow === undefined ? null : <p className="text-sm text-muted-foreground">{eyebrow}</p>;
  if (variant === 'sheet') {
    return (
      <Sheet onOpenChange={onOpenChange} open={open}>
        <SheetContent
          className={sheetWidth(size, fullScreenOnCompact)}
          showCloseButton={false}
          side="right"
        >
          <SheetHeader>
            {eyebrowNode}
            <SheetTitle>{title}</SheetTitle>
            {description === undefined ? null : <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4">{children}</div>
          {footer === undefined ? null : <div className="flex flex-wrap gap-2 p-4">{footer}</div>}
          <DialogClose
            className="absolute top-3 end-3"
            render={<ShadButton size="icon-sm" variant="ghost" />}
          >
            <XIcon aria-hidden="true" />
            <span className="sr-only">بستن</span>
          </DialogClose>
        </SheetContent>
      </Sheet>
    );
  }
  const dialogWidth =
    size === 'wizard'
      ? 'sm:max-w-3xl'
      : size === 'wide'
        ? 'sm:max-w-2xl'
        : size === 'small'
          ? 'sm:max-w-sm'
          : 'sm:max-w-lg';
  return (
    <ShadDialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className={`max-h-[90dvh] overflow-y-auto ${dialogWidth}`}
        showCloseButton={false}
      >
        <DialogHeader>
          {eyebrowNode}
          <DialogTitle>{title}</DialogTitle>
          {description === undefined ? null : <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        {footer === undefined ? null : <DialogFooter>{footer}</DialogFooter>}
        <DialogClose
          className="absolute top-2 end-2"
          render={<ShadButton size="icon-sm" variant="ghost" />}
        >
          <XIcon aria-hidden="true" />
          <span className="sr-only">بستن</span>
        </DialogClose>
      </DialogContent>
    </ShadDialog>
  );
}

export function SideSheet(
  props: Omit<ComponentProps<typeof Dialog>, 'variant'> & { readonly variant?: 'dialog' | 'sheet' },
) {
  return <Dialog {...props} variant="sheet" />;
}

export function DialogActions({
  primary,
  secondary,
  tertiary,
}: {
  readonly primary: ReactNode;
  readonly secondary?: ReactNode;
  readonly tertiary?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {tertiary}
      {secondary}
      {primary}
    </div>
  );
}

export function AlertDialog({
  cancelLabel = 'بازگشت',
  children,
  confirmLabel,
  description,
  destructive = false,
  keepOpenOnConfirm = false,
  onConfirm,
  onOpenChange,
  open,
  title,
}: {
  readonly cancelLabel?: string;
  readonly children?: ReactNode;
  readonly confirmLabel: string;
  readonly description?: ReactNode;
  readonly destructive?: boolean;
  readonly keepOpenOnConfirm?: boolean;
  readonly onConfirm: () => void;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
  readonly title: ReactNode;
}) {
  return (
    <ShadAlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description === undefined ? null : typeof description === 'string' ? (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          ) : (
            <div className="text-sm text-muted-foreground">{description}</div>
          )}
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <ShadButton onClick={() => onOpenChange(false)} type="button" variant="outline">
            {cancelLabel}
          </ShadButton>
          <ShadButton
            onClick={() => {
              onConfirm();
              if (!keepOpenOnConfirm) onOpenChange(false);
            }}
            type="button"
            variant={destructive ? 'destructive' : 'default'}
          >
            {confirmLabel}
          </ShadButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </ShadAlertDialog>
  );
}

export function DiscardChangesDialog({
  onConfirmDiscard,
  onOpenChange,
  open,
}: {
  readonly onConfirmDiscard: () => void;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
}) {
  return (
    <AlertDialog
      confirmLabel="خروج بدون ذخیره"
      description="تغییراتی که ذخیره نشده‌اند از بین می‌روند."
      destructive
      onConfirm={onConfirmDiscard}
      onOpenChange={onOpenChange}
      open={open}
      title="تغییرات ذخیره نشود؟"
    />
  );
}

export function PageHeader({
  actions,
  description,
  eyebrow,
  summary,
  title,
}: {
  readonly actions?: ReactNode;
  readonly description?: ReactNode;
  readonly eyebrow?: ReactNode;
  readonly summary?: ReactNode;
  readonly title: ReactNode;
}) {
  const heading = (
    <header className="saay-page-header flex flex-wrap items-start justify-between gap-3">
      <div>
        {eyebrow === undefined ? null : <p className="text-sm text-muted-foreground">{eyebrow}</p>}
        <h1>{title}</h1>
        {description === undefined ? null : (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions === undefined ? null : (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </header>
  );
  return summary === undefined ? (
    heading
  ) : (
    <>
      {heading}
      {summary}
    </>
  );
}

export function Section({
  actions,
  children,
  description,
  eyebrow,
  id,
  title,
}: {
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly description?: ReactNode;
  readonly eyebrow?: ReactNode;
  readonly id: string;
  readonly title: ReactNode;
}) {
  return (
    <Surface aria-labelledby={id} role="region">
      <CardHeader>
        {eyebrow === undefined ? null : <CardDescription>{eyebrow}</CardDescription>}
        <CardTitle>
          <h2 id={id}>{title}</h2>
        </CardTitle>
        {description === undefined ? null : <CardDescription>{description}</CardDescription>}
        {actions === undefined ? null : (
          <CardAction className="flex flex-wrap items-center gap-2">{actions}</CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">{children}</CardContent>
    </Surface>
  );
}

export function SheetSection({
  actions,
  children,
  id,
  title,
}: {
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly id: string;
  readonly title: ReactNode;
}) {
  return (
    <Surface aria-labelledby={id} role="region">
      <CardHeader>
        <CardTitle>
          <h3 id={id}>{title}</h3>
        </CardTitle>
        {actions === undefined ? null : (
          <CardAction className="flex flex-wrap items-center gap-2">{actions}</CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">{children}</CardContent>
    </Surface>
  );
}

export interface DetailItem {
  readonly key: string;
  readonly label: ReactNode;
  readonly value: ReactNode;
}

/** Label/value rows in a white box on the grey surface (D-93); a nested list stays flat. */
const FACT_ROWS =
  'flex flex-col divide-y divide-border/60 rounded-2xl bg-background px-4 text-sm [&_dl]:bg-transparent [&_dl]:px-0';
const FACT_ROW = 'flex items-baseline justify-between gap-3 py-3';

export function DetailGrid({ items }: { readonly items: readonly DetailItem[] }) {
  return (
    <dl className="grid gap-x-6 gap-y-4 rounded-2xl bg-background p-4 sm:grid-cols-2">
      {items.map((item) => (
        <div className="min-w-0" key={item.key}>
          <dt className="text-xs text-muted-foreground">{item.label}</dt>
          <dd className="mt-1 font-bold">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function FactList({ items }: { readonly items: readonly DetailItem[] }) {
  return (
    <dl className={FACT_ROWS}>
      {items.map((item) => (
        <div className={FACT_ROW} key={item.key}>
          <dt className="text-muted-foreground">{item.label}</dt>
          <dd className="text-end font-medium">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function KeyValueList({
  items,
  label,
}: {
  readonly items: readonly (DetailItem & {
    readonly copyable?: boolean;
    readonly masked?: MaskedKind;
  })[];
  readonly label?: string;
  readonly onCopy?: (value: string) => void;
}) {
  return (
    <dl {...(label === undefined ? {} : { 'aria-label': label })} className={FACT_ROWS}>
      {items.map((item) => (
        <div className={FACT_ROW} key={item.key}>
          <dt className="text-muted-foreground">{item.label}</dt>
          <dd className="text-end font-medium">
            {item.masked !== undefined && typeof item.value === 'string' ? (
              <Masked kind={item.masked} value={item.value} />
            ) : (
              item.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function DiffList({
  items,
  label,
}: {
  readonly items: readonly {
    readonly changed?: boolean;
    readonly key: string;
    readonly label: ReactNode;
    readonly next?: ReactNode;
    readonly previous?: ReactNode;
  }[];
  readonly label: string;
}) {
  return (
    <div aria-label={label} className={FACT_ROWS} role="list">
      {items.map((item) => (
        <div className="flex flex-col gap-1 py-3" key={item.key} role="listitem">
          <div className="text-xs text-muted-foreground">{item.label}</div>
          {item.changed ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground">{item.previous ?? '—'}</span>
              <span aria-hidden="true">←</span>
              <span className="font-bold">{item.next ?? '—'}</span>
            </div>
          ) : (
            <div className="font-medium">{item.next ?? item.previous ?? '—'}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export function StatusChip({
  enumName,
  title,
  tone: toneOverride,
  value,
}: {
  readonly className?: string;
  readonly enumName?: StatusEnum;
  readonly icon?: string;
  readonly meta?: ReactNode;
  readonly title?: ReactNode;
  readonly tone?: StatusTone | 'on-card';
  readonly value?: string;
}) {
  const resolved =
    enumName !== undefined && value !== undefined
      ? resolveStatus(enumName, value)
      : { known: true, labelFa: '', tone: 'neutral' as StatusTone };
  const text = title ?? resolved.labelFa;
  const tone = toneOverride ?? (title === undefined ? resolved.tone : 'neutral');
  return (
    <StatusBadge tone={STATUS_TONE[tone === 'on-card' ? 'neutral' : tone]}>{text}</StatusBadge>
  );
}

/** Status dictionary tones on the v2 brand tones (D-93): danger stays for errors and overdue. */
export const STATUS_TONE: Readonly<Record<StatusTone, BlockTone>> = {
  danger: 'danger',
  info: 'forest',
  neutral: 'forest',
  success: 'olive',
  warning: 'clay',
};

const COUNT_TONE: Readonly<Record<StatusTone, BlockTone>> = { ...STATUS_TONE, info: 'gold' };

export function CountBadge({
  children,
  tone = 'neutral',
}: {
  readonly children: ReactNode;
  readonly tone?: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
}) {
  return <StatusBadge tone={COUNT_TONE[tone]}>{children}</StatusBadge>;
}

function barPercent(rate: string): number {
  const value = Number(rate);
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value * 100));
}

export function CapacityMeter({
  label,
  rate,
  valueText,
}: {
  readonly label: string;
  readonly rate: string | null;
  readonly valueText: string;
}) {
  const percent = rate === null ? 0 : barPercent(rate);
  return (
    <div className="flex flex-col gap-2">
      <Progress aria-label={label} aria-valuetext={valueText} value={Math.round(percent)} />
      <span className="text-xs text-muted-foreground tabular-nums">{valueText}</span>
    </div>
  );
}

export function LinearProgress({
  indeterminate = false,
  label,
  value,
}: {
  readonly 'aria-label'?: string;
  readonly indeterminate?: boolean;
  readonly label: string;
  readonly value?: number;
}) {
  const percent = value === undefined ? 0 : Math.min(100, Math.max(0, value));
  return (
    <Progress
      aria-label={label}
      className="[&_[data-slot=progress-track]]:h-2 [&_[data-slot=progress-track]]:bg-muted-foreground/35"
      value={indeterminate ? null : percent}
    />
  );
}

export function ProfileHeader({
  badge,
  headingId,
  name,
  subtitle,
}: {
  readonly badge?: ReactNode;
  readonly headingId: string;
  readonly name: string;
  readonly subtitle?: ReactNode;
}) {
  const initial = [...name.trim()][0] ?? 'S';
  return (
    <div className="flex items-center gap-3">
      <Avatar aria-hidden="true" size="lg">
        <AvatarFallback className="font-bold">{initial}</AvatarFallback>
      </Avatar>
      <div>
        <h2 className="text-lg font-bold" id={headingId}>
          {name}
        </h2>
        {subtitle === undefined ? null : (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {badge}
    </div>
  );
}

export function StatGrid({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label?: string;
}) {
  return (
    <section
      {...(label === undefined ? {} : { 'aria-label': label })}
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
    >
      {children}
    </section>
  );
}

export function StatTile({
  action,
  hint,
  label,
  value,
}: {
  readonly action?: ReactNode;
  readonly hint?: ReactNode;
  readonly href?: string;
  readonly icon?: string;
  readonly label: ReactNode;
  readonly tone?: string;
  readonly value: ReactNode;
}) {
  return (
    <Surface>
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        {action === undefined ? null : <CardAction>{action}</CardAction>}
      </CardHeader>
      <CardContent className="text-2xl font-extrabold tabular-nums">{value}</CardContent>
      {hint === undefined ? null : (
        <CardContent>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </CardContent>
      )}
    </Surface>
  );
}

export function MiniStats({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label?: string;
}) {
  return <StatGrid {...(label === undefined ? {} : { label })}>{children}</StatGrid>;
}

export function MiniStat({
  label,
  value,
}: {
  readonly label: ReactNode;
  readonly value: ReactNode;
}) {
  return (
    <article>
      <Surface className="h-full">
        <CardContent className="flex flex-col gap-2">
          <span className="text-sm text-muted-foreground">{label}</span>
          <strong className="text-2xl font-extrabold tabular-nums">{value}</strong>
        </CardContent>
      </Surface>
    </article>
  );
}

export function CreditCard({
  amount,
  amountLabel,
  badge,
  details,
  footer,
  title,
  unit,
}: {
  readonly amount: ReactNode;
  readonly amountLabel: ReactNode;
  readonly badge?: ReactNode;
  readonly details?: readonly (Omit<DetailItem, 'label'> & { readonly label: string })[];
  readonly footer?: ReactNode;
  readonly title: ReactNode;
  readonly unit?: ReactNode;
}) {
  return (
    <SignatureCard
      badge={badge}
      header={
        <div className="flex items-center justify-between gap-3 text-sm font-bold">
          <span>{title}</span>
          <span dir="ltr" translate="no">
            Grab &amp; Go
          </span>
        </div>
      }
      label={amountLabel}
      value={
        <>
          {amount}
          {unit === undefined ? null : <span className="ms-1 text-sm font-medium">{unit}</span>}
        </>
      }
    >
      {details === undefined ? null : <CardFaceRows items={details} />}
      {footer}
    </SignatureCard>
  );
}

export function Tabs<K extends string>({
  label,
  onChange,
  panel,
  tabs,
  value,
}: {
  readonly label: string;
  readonly onChange: (key: K) => void;
  readonly panel: ReactNode;
  readonly tabs: readonly { readonly key: K; readonly label: string }[];
  readonly value: K;
}) {
  return (
    <ShadTabs onValueChange={(next) => onChange(next as K)} value={value}>
      <TabsList aria-label={label}>
        {tabs.map((tab) => (
          <TabsTrigger key={tab.key} value={tab.key}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value={value}>{panel}</TabsContent>
    </ShadTabs>
  );
}

export function WizardStepper({
  current,
  label,
  onSelect,
  steps,
}: {
  readonly current: number;
  readonly label: string;
  readonly onSelect: (step: number) => void;
  readonly steps: readonly { readonly description?: string; readonly title: string }[];
}) {
  const percent = steps.length === 0 ? 0 : Math.round(((current + 1) / steps.length) * 100);
  return (
    <>
      <ol aria-label={label} className="grid gap-2">
        {steps.map((step, index) => (
          <li aria-current={index === current ? 'step' : undefined} key={step.title}>
            <ShadButton
              disabled={index > current}
              onClick={() => onSelect(index)}
              type="button"
              variant={index === current ? 'secondary' : 'ghost'}
            >
              <span aria-hidden="true">{toPersianDigits(index + 1)}</span>
              <span>
                <strong>{step.title}</strong>
                {step.description === undefined ? null : <small>{step.description}</small>}
              </span>
            </ShadButton>
          </li>
        ))}
      </ol>
      <div aria-hidden="true">
        <span>{`مرحله ${toPersianDigits(current + 1)} از ${toPersianDigits(steps.length)}`}</span>
        <span style={{ width: `${percent}%` }} />
      </div>
    </>
  );
}

type TableColumn<Row> = {
  readonly detail?: boolean;
  readonly header: ReactNode;
  readonly key: string;
  readonly numeric?: boolean;
  readonly pin?: 'start' | 'end';
  readonly render: (row: Row) => ReactNode;
};

export function Table<Row>({
  caption,
  columns,
  recordDetails,
  regionLabel,
  rowKey,
  rows,
}: {
  readonly caption: string;
  readonly columns: readonly TableColumn<Row>[];
  readonly recordDetails?: {
    readonly label?: (row: Row) => string;
    readonly title: (row: Row) => ReactNode;
  };
  readonly regionLabel?: string;
  readonly rowKey: (row: Row) => string;
  readonly rows: readonly Row[];
}) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const openRow = openKey === null ? undefined : rows.find((row) => rowKey(row) === openKey);
  const detailColumns = columns.filter((column) => column.detail !== false);
  return (
    <>
      <div
        aria-label={regionLabel ?? caption}
        className="w-full overflow-x-auto rounded-2xl bg-background [&_td:first-child]:ps-4 [&_td:last-child]:pe-4 [&_th:first-child]:ps-4 [&_th:last-child]:pe-4"
        role="region"
        tabIndex={0}
      >
        <ShadTable>
          <caption className="sr-only">{caption}</caption>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  className={column.numeric === true ? 'text-end' : undefined}
                  key={column.key}
                >
                  {column.header}
                </TableHead>
              ))}
              {recordDetails === undefined ? null : (
                <TableHead>
                  <span className="sr-only">جزئیات</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const key = rowKey(row);
              return (
                <TableRow key={key}>
                  {columns.map((column) => (
                    <TableCell
                      className={column.numeric === true ? 'text-end tabular-nums' : undefined}
                      key={column.key}
                    >
                      {column.render(row)}
                    </TableCell>
                  ))}
                  {recordDetails === undefined ? null : (
                    <TableCell>
                      <ShadButton
                        aria-label={recordDetails.label?.(row) ?? 'مشاهده جزئیات ردیف'}
                        onClick={() => setOpenKey(key)}
                        size="icon-sm"
                        type="button"
                        variant="ghost"
                      >
                        <EyeIcon aria-hidden="true" />
                      </ShadButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </ShadTable>
      </div>
      {recordDetails === undefined || openRow === undefined ? null : (
        <Dialog
          eyebrow="جزئیات ردیف"
          onOpenChange={(next) => {
            if (!next) setOpenKey(null);
          }}
          open
          title={recordDetails.title(openRow)}
        >
          <dl className="grid gap-2">
            {detailColumns.map((column) => (
              <div key={column.key}>
                <dt className="text-sm text-muted-foreground">{column.header}</dt>
                <dd>{column.render(openRow)}</dd>
              </div>
            ))}
          </dl>
        </Dialog>
      )}
    </>
  );
}

export function LoadMore({
  hasMore,
  loading,
  onLoadMore,
}: {
  readonly announceCount?: number;
  readonly autoLoadOnCompact?: boolean;
  readonly hasMore: boolean;
  readonly loading: boolean;
  readonly onLoadMore: () => void;
}) {
  if (!hasMore) return null;
  return (
    <ShadButton disabled={loading} onClick={onLoadMore} type="button" variant="outline">
      نمایش موارد بیشتر
    </ShadButton>
  );
}

const MenuActions = createContext<(() => void) | null>(null);

export function MenuItem({
  children,
  destructive = false,
  disabled = false,
  disabledReason,
  onSelect,
}: {
  readonly children: ReactNode;
  readonly destructive?: boolean;
  readonly disabled?: boolean;
  readonly disabledReason?: string;
  readonly onSelect?: () => void;
  readonly selected?: boolean;
}) {
  const close = useContext(MenuActions);
  return (
    <DropdownMenuItem
      className="min-h-9 flex-col items-start gap-0.5 rounded-lg px-3 py-2"
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onSelect?.();
        close?.();
      }}
      variant={destructive ? 'destructive' : 'default'}
    >
      <span>{children}</span>
      {disabled && disabledReason !== undefined ? (
        <span className="text-xs text-muted-foreground">{disabledReason}</span>
      ) : null}
    </DropdownMenuItem>
  );
}

export function Menu({
  align = 'end',
  children,
  label,
  onOpenChange,
  open,
  trigger,
}: {
  readonly align?: 'start' | 'end';
  readonly children: ReactNode;
  readonly label: string;
  readonly onOpenChange?: (open: boolean) => void;
  readonly open?: boolean;
  readonly trigger: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;

  function setOpen(next: boolean) {
    if (open === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  }

  const triggerNode = isValidElement(trigger)
    ? cloneElement(trigger, {
        'aria-expanded': isOpen,
        'aria-haspopup': 'menu',
        'aria-label': trigger.props['aria-label'] ?? label,
        onClick: (event: MouseEvent<HTMLButtonElement>) => {
          trigger.props.onClick?.(event);
          setOpen(!isOpen);
        },
      })
    : trigger;

  return (
    <DropdownMenu modal={false} onOpenChange={() => undefined} open={isOpen}>
      <DropdownMenuTrigger render={triggerNode} />
      <DropdownMenuContent align={align} className="min-w-48 p-1.5">
        <MenuActions.Provider value={() => setOpen(false)}>{children}</MenuActions.Provider>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SegmentedButtons<K extends string>({
  disabled = false,
  label,
  mode = 'single',
  onChange,
  options,
  value,
}: {
  readonly disabled?: boolean;
  readonly label: string;
  readonly mode?: 'single' | 'multiple';
  readonly onChange: (next: K | readonly K[]) => void;
  readonly options: readonly {
    readonly disabled?: boolean;
    readonly icon?: string;
    readonly key: K;
    readonly label: string;
    readonly loading?: boolean;
  }[];
  readonly value: K | readonly K[] | undefined;
}) {
  const selected = new Set(
    mode === 'multiple'
      ? Array.isArray(value)
        ? value
        : value === undefined
          ? []
          : [value]
      : value === undefined
        ? []
        : [value as K],
  );
  if (mode === 'multiple') {
    return (
      <div aria-label={label} className="flex flex-wrap gap-2" role="group">
        {options.map((option) => {
          const active = selected.has(option.key);
          return (
            <ShadButton
              aria-pressed={active}
              disabled={disabled || option.disabled === true || option.loading === true}
              key={option.key}
              onClick={() => {
                const next = new Set(selected);
                if (next.has(option.key)) next.delete(option.key);
                else next.add(option.key);
                onChange([...next]);
              }}
              type="button"
              variant={active ? 'secondary' : 'outline'}
            >
              {option.label}
            </ShadButton>
          );
        })}
      </div>
    );
  }
  return (
    <RadioGroup
      aria-label={label}
      className="flex flex-row flex-wrap gap-2"
      disabled={disabled}
      onValueChange={(next) => {
        if (typeof next === 'string') onChange(next as K);
      }}
      value={typeof value === 'string' ? value : ''}
    >
      {options.map((option) => (
        <FieldLabel className="w-auto" key={option.key}>
          <RadioGroupItem
            disabled={option.disabled === true || option.loading === true}
            value={option.key}
          />
          <span>{option.label}</span>
        </FieldLabel>
      ))}
    </RadioGroup>
  );
}

export function SegmentedControl<K extends string>({
  label,
  onChange,
  options,
  value,
}: {
  readonly label: string;
  readonly onChange: (key: K) => void;
  readonly options: readonly { readonly key: K; readonly label: string }[];
  readonly value: K | undefined;
}) {
  return (
    <SegmentedButtons
      label={label}
      onChange={(next) => {
        if (typeof next === 'string') onChange(next as K);
      }}
      options={options}
      value={value}
    />
  );
}

export function ChoiceCards({
  disabled = false,
  error,
  hint,
  legend,
  name,
  onChange,
  options,
  value,
}: {
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly legend: ReactNode;
  readonly name: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly {
    readonly description?: ReactNode;
    readonly label: ReactNode;
    readonly value: string;
  }[];
  readonly value: string | undefined;
}) {
  return (
    <fieldset className="grid gap-2" disabled={disabled}>
      <legend>{legend}</legend>
      <RadioGroup
        name={name}
        onValueChange={(next) => {
          if (typeof next === 'string') onChange(next);
        }}
        value={value ?? ''}
      >
        {options.map((option) => (
          <Field key={option.value}>
            <FieldLabel className="w-auto">
              <RadioGroupItem value={option.value} />
              <span>{option.label}</span>
            </FieldLabel>
            {option.description === undefined ? null : (
              <FieldDescription>{option.description}</FieldDescription>
            )}
          </Field>
        ))}
      </RadioGroup>
      {hint === undefined ? null : <FieldDescription>{hint}</FieldDescription>}
      {error === undefined ? null : <FieldError>{error}</FieldError>}
    </fieldset>
  );
}

export function Switch({
  checked,
  disabled = false,
  id,
  label,
  onCheckedChange,
  pending = false,
}: {
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly label: ReactNode;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly pending?: boolean;
}) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <Field className="w-auto" orientation="horizontal">
      <ShadSwitch
        checked={checked}
        data-state={checked ? 'checked' : 'unchecked'}
        disabled={disabled || pending}
        id={fieldId}
        onCheckedChange={(next) => onCheckedChange(next)}
      />
      <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
    </Field>
  );
}

export const Checkbox = forwardRef<
  HTMLInputElement,
  {
    readonly error?: string | undefined;
    readonly hint?: ReactNode;
    readonly indeterminate?: boolean;
    readonly label: ReactNode;
  } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function Checkbox(
  { checked, defaultChecked, disabled, error, hint, id, indeterminate, label, onChange, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const controlled = checked !== undefined;
  const [uncontrolled, setUncontrolled] = useState(defaultChecked === true);
  const current = controlled ? checked === true : uncontrolled;

  useEffect(() => {
    if (inputRef.current !== null) inputRef.current.indeterminate = indeterminate === true;
  }, [current, indeterminate]);

  function assign(node: HTMLInputElement | null) {
    inputRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref !== null) ref.current = node;
  }

  function commit(next: boolean) {
    if (!controlled) setUncontrolled(next);
    const input = inputRef.current;
    if (input !== null) {
      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'checked')?.set;
      setter?.call(input, next);
    }
    onChange?.({
      currentTarget: input ?? ({ checked: next, type: 'checkbox' } as HTMLInputElement),
      target:
        input ?? ({ checked: next, name: rest.name ?? '', type: 'checkbox' } as HTMLInputElement),
    } as ChangeEvent<HTMLInputElement>);
  }

  return (
    <Field data-invalid={error === undefined ? undefined : true}>
      <FieldLabel htmlFor={fieldId}>
        <ShadCheckbox
          aria-invalid={error === undefined ? undefined : true}
          checked={current}
          disabled={disabled}
          id={fieldId}
          onCheckedChange={(next) => commit(next === true)}
        />
        <span>{label}</span>
      </FieldLabel>
      <input
        {...rest}
        aria-hidden
        checked={controlled ? current : undefined}
        className="sr-only"
        defaultChecked={controlled ? undefined : defaultChecked}
        disabled={disabled}
        onChange={onChange}
        ref={assign}
        tabIndex={-1}
        type="checkbox"
      />
      {hint === undefined ? null : <FieldDescription>{hint}</FieldDescription>}
      {error === undefined ? null : <FieldError>{error}</FieldError>}
    </Field>
  );
});

function fieldLabel(label: ReactNode, optional: boolean): ReactNode {
  return optional && typeof label === 'string' ? `${label} (اختیاری)` : label;
}

export const TextField = forwardRef<
  HTMLInputElement,
  {
    readonly clearable?: boolean;
    readonly error?: string | undefined;
    readonly hint?: ReactNode;
    readonly label: ReactNode;
    readonly leadingIcon?: string;
    readonly ltr?: boolean;
    readonly mask?: MaskSpec;
    readonly maskRaw?: string;
    readonly onMaskRawChange?: (raw: string) => void;
    readonly optional?: boolean;
    readonly prefix?: ReactNode;
    readonly suffix?: ReactNode;
    readonly trailingValid?: boolean;
    readonly id?: string;
    readonly variant?: string;
  } & Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'prefix'>
>(function TextField(
  {
    error,
    hint,
    id,
    label,
    ltr,
    mask,
    maskRaw,
    onChange,
    onMaskRawChange,
    optional = false,
    prefix,
    suffix,
    trailingValid = false,
    clearable: _clearable,
    leadingIcon: _leadingIcon,
    variant: _variant,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const invalid = error !== undefined;
  const masked = mask !== undefined;
  const control = (
    <InputGroup className="w-full">
      {prefix === undefined ? null : <InputGroupAddon>{prefix}</InputGroupAddon>}
      <InputGroupInput
        {...rest}
        aria-invalid={invalid || undefined}
        dir={ltr === true ? 'ltr' : rest.dir}
        id={fieldId}
        onChange={(event) => {
          if (masked && mask !== undefined) {
            onMaskRawChange?.(mask.extractDigits(event.target.value));
            return;
          }
          onChange?.(event);
        }}
        ref={ref}
        value={
          masked && mask !== undefined
            ? maskRaw === undefined || maskRaw === ''
              ? ''
              : toPersianDigits(mask.formatDigits(maskRaw))
            : rest.value
        }
      />
      {suffix === undefined && !trailingValid ? null : (
        <InputGroupAddon align="inline-end">
          {suffix}
          {trailingValid ? <CheckIcon aria-hidden="true" /> : null}
        </InputGroupAddon>
      )}
    </InputGroup>
  );
  return (
    <Field data-invalid={invalid || undefined}>
      <FieldLabel htmlFor={fieldId}>{fieldLabel(label, optional)}</FieldLabel>
      {control}
      {hint === undefined ? null : <FieldDescription>{hint}</FieldDescription>}
      {error === undefined ? null : <FieldError>{error}</FieldError>}
    </Field>
  );
});

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  {
    readonly error?: string | undefined;
    readonly hint?: ReactNode;
    readonly id?: string;
    readonly label: ReactNode;
    readonly optional?: boolean;
  } & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'>
>(function TextArea({ error, hint, id, label, optional = false, ...rest }, ref) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <Field data-invalid={error === undefined ? undefined : true}>
      <FieldLabel htmlFor={fieldId}>{fieldLabel(label, optional)}</FieldLabel>
      <Textarea
        {...rest}
        aria-invalid={error === undefined ? undefined : true}
        id={fieldId}
        ref={ref}
      />
      {hint === undefined ? null : <FieldDescription>{hint}</FieldDescription>}
      {error === undefined ? null : <FieldError>{error}</FieldError>}
    </Field>
  );
});

export const NativeSelect = forwardRef<
  HTMLSelectElement,
  {
    readonly error?: string | undefined;
    readonly hint?: ReactNode;
    readonly label: ReactNode;
    readonly options: readonly {
      readonly disabled?: boolean;
      readonly label: string;
      readonly value: string;
    }[];
    readonly placeholder?: string;
  } & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'>
>(function NativeSelect(
  { error, hint, id, label, options, placeholder, size: _size, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <Field data-invalid={error === undefined ? undefined : true}>
      <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
      <ShadNativeSelect
        {...rest}
        aria-invalid={error === undefined ? undefined : true}
        className="w-full"
        id={fieldId}
        ref={ref}
      >
        {placeholder === undefined ? null : (
          <NativeSelectOption value="">{placeholder}</NativeSelectOption>
        )}
        {options.map((option) => (
          <NativeSelectOption disabled={option.disabled} key={option.value} value={option.value}>
            {option.label}
          </NativeSelectOption>
        ))}
      </ShadNativeSelect>
      {hint === undefined ? null : <FieldDescription>{hint}</FieldDescription>}
      {error === undefined ? null : <FieldError>{error}</FieldError>}
    </Field>
  );
});

export const SearchField = forwardRef<
  HTMLInputElement,
  { readonly invalid?: boolean; readonly label: string } & Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'type' | 'aria-label'
  >
>(function SearchField({ invalid = false, label, onKeyDown, ...rest }, ref) {
  return (
    <Input
      {...rest}
      aria-invalid={invalid || undefined}
      aria-label={label}
      className="w-full min-w-0 sm:w-auto sm:min-w-48 sm:max-w-sm sm:flex-1"
      enterKeyHint="search"
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.key === 'Enter' && !event.defaultPrevented && !event.nativeEvent.isComposing) {
          event.preventDefault();
          event.currentTarget.form?.requestSubmit();
        }
      }}
      ref={ref}
      type="search"
    />
  );
});

export const FilterSelect = forwardRef<
  HTMLSelectElement,
  {
    readonly label: string;
    readonly options: readonly { readonly label: string; readonly value: string }[];
    readonly placeholder: string;
  } & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'aria-label'>
>(function FilterSelect({ label, options, placeholder, size: _size, ...rest }, ref) {
  return (
    <ShadNativeSelect
      {...rest}
      aria-label={label}
      className="w-full min-w-0 sm:w-auto sm:min-w-36"
      ref={ref}
    >
      <NativeSelectOption value="">{placeholder}</NativeSelectOption>
      {options.map((option) => (
        <NativeSelectOption key={option.value} value={option.value}>
          {option.label}
        </NativeSelectOption>
      ))}
    </ShadNativeSelect>
  );
});

export function FilterChip({
  children,
  disabled = false,
  onRemove,
  onToggle,
  selected = false,
}: {
  readonly children: ReactNode;
  readonly disabled?: boolean;
  readonly onRemove?: () => void;
  readonly onToggle?: () => void;
  readonly selected?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <ShadButton
        aria-pressed={selected}
        disabled={disabled}
        onClick={onToggle}
        size="sm"
        type="button"
        variant={selected ? 'secondary' : 'outline'}
      >
        {children}
      </ShadButton>
      {onRemove === undefined ? null : (
        <ShadButton
          aria-label="حذف فیلتر"
          onClick={onRemove}
          size="sm"
          type="button"
          variant="ghost"
        >
          حذف
        </ShadButton>
      )}
    </span>
  );
}

export function AssistChip({
  children,
  onClick,
}: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
}) {
  return (
    <ShadButton onClick={onClick} size="sm" type="button" variant="outline">
      {children}
    </ShadButton>
  );
}

export function ChipRow({ children }: { readonly children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

export function ReasonChips({
  onChange,
  reasons,
  value,
}: {
  readonly onChange: (next: string) => void;
  readonly reasons: readonly string[];
  readonly value: string;
}) {
  return (
    <ChipRow>
      {reasons.map((reason) => (
        <AssistChip
          key={reason}
          onClick={() => {
            const trimmed = value.trim();
            onChange(trimmed.length === 0 ? reason : `${trimmed} ${reason}`);
          }}
        >
          {reason}
        </AssistChip>
      ))}
    </ChipRow>
  );
}

export function AccordionPanel({
  defaultOpenKeys,
  items,
  label,
  type = 'single',
}: {
  readonly defaultOpenKeys?: readonly string[];
  readonly headingLevel?: 2 | 3 | 4;
  readonly items: readonly {
    readonly content: ReactNode;
    readonly key: string;
    readonly summary?: ReactNode;
    readonly title: ReactNode;
  }[];
  readonly label: string;
  readonly type?: 'single' | 'multiple';
}) {
  return (
    <Accordion
      aria-label={label}
      defaultValue={[...(defaultOpenKeys ?? [])]}
      {...(type === 'multiple' ? {} : {})}
    >
      {items.map((item) => (
        <AccordionItem key={item.key} value={item.key}>
          <AccordionTrigger>
            <span className="flex flex-1 items-center justify-between gap-2">
              <span>{item.title}</span>
              {item.summary}
            </span>
          </AccordionTrigger>
          <AccordionContent>{item.content}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function List({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label?: string;
}) {
  return (
    <ul
      {...(label === undefined ? {} : { 'aria-label': label })}
      className="flex flex-col gap-1 self-start rounded-3xl bg-card p-2"
    >
      {children}
    </ul>
  );
}

/** A list row: white on hover, primary green when it is the current one (D-93). */
const LIST_ROW =
  'flex w-full items-center gap-3 rounded-2xl bg-background px-3 py-2.5 text-start text-sm font-medium transition-colors hover:bg-muted aria-[current=true]:bg-primary aria-[current=true]:text-primary-foreground';

export function ListItem(
  props:
    | {
        readonly as?: 'div';
        readonly leading?: ReactNode;
        readonly onClick?: () => void;
        readonly primary: ReactNode;
        readonly secondary?: ReactNode;
        readonly selected?: boolean;
        readonly tertiary?: ReactNode;
        readonly trailing?: ReactNode;
      }
    | {
        readonly as: 'button';
        readonly leading?: ReactNode;
        readonly onClick: () => void;
        readonly primary: ReactNode;
        readonly secondary?: ReactNode;
        readonly selected?: boolean;
        readonly tertiary?: ReactNode;
        readonly trailing?: ReactNode;
      }
    | {
        readonly as: 'a';
        readonly href: string;
        readonly leading?: ReactNode;
        readonly primary: ReactNode;
        readonly secondary?: ReactNode;
        readonly selected?: boolean;
        readonly tertiary?: ReactNode;
        readonly trailing?: ReactNode;
      },
) {
  const { leading, primary, secondary, selected = false, tertiary, trailing } = props;
  const body = (
    <>
      {leading}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span>{primary}</span>
        {secondary === undefined ? null : <span className="text-xs opacity-80">{secondary}</span>}
        {tertiary === undefined ? null : <span className="text-xs opacity-80">{tertiary}</span>}
      </span>
      {trailing}
    </>
  );
  if (props.as === 'a') {
    return (
      <li>
        <a aria-current={selected ? 'true' : undefined} className={LIST_ROW} href={props.href}>
          {body}
        </a>
      </li>
    );
  }
  if (props.as === 'button' || props.onClick !== undefined) {
    return (
      <li>
        <button
          aria-current={selected ? 'true' : undefined}
          className={LIST_ROW}
          onClick={props.onClick}
          type="button"
        >
          {body}
        </button>
      </li>
    );
  }
  return <li className={LIST_ROW}>{body}</li>;
}

export function CopyableIdentifier({
  copyLabel,
  label,
  masked,
  value,
}: {
  readonly copyLabel?: string;
  readonly label: ReactNode;
  readonly masked?: string;
  readonly value: string;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span>{label ?? <Ltr>{masked ?? value}</Ltr>}</span>
      <ShadButton
        aria-label={copyLabel ?? `کپی ${masked ?? value}`}
        onClick={() => {
          void navigator.clipboard?.writeText(value);
        }}
        size="sm"
        type="button"
        variant="ghost"
      >
        کپی
      </ShadButton>
    </span>
  );
}

export function AlertList({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label?: string;
}) {
  return (
    <ul {...(label === undefined ? {} : { 'aria-label': label })} className="grid gap-2">
      {children}
    </ul>
  );
}

export function AlertItem({
  action,
  icon,
  text,
  title,
}: {
  readonly action?: ReactNode;
  readonly icon?: string;
  readonly text?: ReactNode;
  readonly title: ReactNode;
}) {
  const Glyph = icon === undefined ? undefined : ICONS[icon];
  return (
    <li>
      <Alert className="rounded-2xl border-0 bg-background p-3">
        {Glyph === undefined ? null : <Glyph aria-hidden="true" />}
        <AlertTitle className="font-bold">{title}</AlertTitle>
        {text === undefined ? null : <AlertDescription>{text}</AlertDescription>}
        {action === undefined ? null : <div className="col-span-full mt-2">{action}</div>}
      </Alert>
    </li>
  );
}

export function DateRangePicker(props: ComponentProps<typeof SolarDateRangePicker>) {
  return (
    <Field className="w-full">
      <SolarDateRangePicker {...props} />
    </Field>
  );
}

export function PanelGrid({
  children,
}: {
  readonly children: ReactNode;
  readonly layout?: string;
}) {
  return <div className="grid gap-4 xl:grid-cols-2">{children}</div>;
}

export function PanelStack({ children }: { readonly children: ReactNode }) {
  return <div className="grid gap-4">{children}</div>;
}
